import { z } from "zod"
// https://www.switchsolar.io/api-reference/savings-api/savings-analysis/

export const accountAnalysisSummarySchema = z.object({
  /**
   * Pre-Solar Summary Fields
   * These fields summarize values for how things would have
   * been in the first year without solar.
   */

  /**
   * The first year, pre-solar cost of energy.
   * It is the highest of the preTotalNonMinCost,
   * preTotalMinCost, and preTotalNonBypassableCost.

   */
  preTotalCost: z.number(),

  /**
   * The first year, pre-solar amount of energy used.
   */
  preTotalKWh: z.number(),

  /**
   * The first year, pre-solar cost of kWh only.
   * Does not include minimum costs.
   */
  preTotalKWhCost: z.number(),

  /**
   * The first year, pre-solar rate for kWh only.
   * Does not include minimum costs.
   */
  preTotalKWhRate: z.number(),

  /**
   * The first year minimum bill for the customer.
   */
  preTotalMinCost: z.number(),

  /**
   * The first year non-passable costs for the customer.

   */
  preTotalNonBypassableCost: z.number(),

  /**
   * The first year bill for the customer.
   * Will almost always be the same as preTotalCost.
   */
  preTotalNonMinCost: z.number(),

  /**
   * Pre-solar blended kWh rate.
   * Equal to (preTotalCost/preTotalkWh)
   */
  preTotalRate: z.number(),

  /**
   * Post-Solar Summary Fields
   * These fields represent the first year after solar.
   * Not all of these values will show up in every situation.
   * For example, netAvoidedRate won’t show up if you haven’t
   * included a solar profile, as there’s no “avoided” rate
   * to calculate. For a standard solar analysis, though,
   * these values will always be available.
   */

  /**
   * The first year, post-solar cost of energy from the utility only
   * (i.e. not including the cost of solar).
   * It is the highest of the postTotalNonMinCost,
   * postTotalMinCost, and postTotalNonBypassableCost.
   * Type: number
   */
  postTotalCost: z.number(),

  /**
   * The amount of energy still consumed from the utility
   * in the first year after solar.
   * Type: number
   */
  postTotalKWh: z.number(),

  /**
   * The first year post-solar cost for kWh only.
   * Does not include minimum costs.
   * Type: number
   */
  postTotalKWhCost: z.number(),

  /**
   * The first-year post-solar rate for energy from the utility only.
   * Equal to (postTotalKWhCost/postTotalKWh).
   * Type: number
   */
  postTotalKWhRate: z.number(),

  /**
   * The minimum bill for the first year.
   * Type: number
   */
  postTotalMinCost: z.number(),

  /**
   * The minimum bill for the first year, calculated using
   * NonBypassable Charges (NBCs). Non-Bypassable charges
   * cannot be offset by other NEM credits, creating a
   * second minimum charge for solar customers.
   * Type: number
   */
  postTotalNonBypassableCost: z.number().optional(),

  /**
   * The customer’s new utility bill, without accounting for any minimum charges.
   * Type: number
   */
  postTotalNonMinCost: z.number().optional(),

  /**
   * The post-solar blended kWh rate for energy still purchased from the utility.
   * Equal to (postTotalCost/postTotalKWh).
   * Type: number
   */
  postTotalRate: z.number().optional(),

  /**
   * "Net" Summary Fields
   * Net fields represent the difference between the pre- and post- solar values.
   */

  /**
   * The cost of energy that is no longer being purchased
   * from the utility in the first year post-solar.
   * Equal to (preTotalCost - postTotalCost).
   * Type: number
   */
  netAvoidedCost: z.number().optional(),

  /**
   * The percentage of first-year cost from the utility offset by solar.
   * Equal to (netAvoidedCost/preTotalCost).
   * Type: number
   */
  netAvoidedCostPctOffset: z.number().optional(),

  /**
   * The amount of kWh generated by the solar power system.
   * Equal to (preTotalkWh - postTotalkWh).
   * Type: number
   */
  netAvoidedKWh: z.number().optional(),

  /**
   * The percentage of first-year kWh offset by solar.
   * Equal to (netAvoidedkWh/preTotalkWh).
   * Type: number
   */
  netAvoidedKWhPctOffset: z.number().optional(),

  /**
   * The average value of an avoided kWh.
   * Equal to (netAvoidedCost/netAvoidedKWh).
   * Type: number
   */
  netAvoidedRate: z.number().optional(),

  /**
   * Lifetime Summary Fields
   * Lifetime values represent totals over the life of the project.
   */

  /**
   * The cost of solar energy over the life of the project.
   * Type: number
   */
  lifetimeSolarCost: z.number().optional(),

  /**
   * ACP over the life of the project.
   * Type: number
   */
  lifeTimeUtilityAvoidedRate: z.number().optional(),

  /**
   * The life time cost of energy without a solar system.
   * Type: number
   */
  lifetimeWithoutCost: z.number().optional(),

  /**
   * Customer savings over the life of the project.
   * Equal to:
   * - (lifetimeWithoutCost - [lifetimeSolarCost + lifeTimeUtilityAfterCost]).
   * Type: number
   */
  lifetimeAvoidedCost: z.number().optional(),

  /**
   * The amount still paid to the utility after
   * solar over the life of the project.
   * Type: number
   */
  lifeTimeUtilityAfterCost: z.number().optional(),

  postTotalLimitedExportCredit: z.number().optional(),
  postTotalLimitedExportCreditKWh: z.number().optional(),
  postTotalLimitedExportCreditRate: z.number().optional(),
  preTotalMinIneligibleCost: z.number().optional(),
})

export type AccountAnalysisSummary = z.infer<typeof accountAnalysisSummarySchema>
